import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ASSETS } from "@/lib/brand";

const CLAUDIO_VIDEO =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_cde26d2e-c3c8-4554-9b09-3a5e26da6f87/artifacts/dpw5bec7_video%20promo%20blanche%20lungo.mp4";

function FounderVideo() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleAudio = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <div className="relative aspect-[9/16] w-full max-w-[420px] mx-auto lg:mx-0 bg-[#0A0A0A] overflow-hidden">
      <video
        ref={videoRef}
        src={CLAUDIO_VIDEO}
        poster={ASSETS.claudio}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        className="w-full h-full object-cover"
        data-testid="philosophy-video"
      />
      <button
        onClick={toggleAudio}
        aria-label={muted ? "Attiva audio" : "Disattiva audio"}
        data-testid="philosophy-video-audio-toggle"
        className="absolute bottom-4 right-4 flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-md transition-colors duration-300"
        style={{
          background: "rgba(10,10,10,0.55)",
          border: "1px solid rgba(196,166,113,0.6)",
          color: "#C4A671",
        }}
      >
        <span className="text-sm leading-none">{muted ? "♪" : "𝄐"}</span>
        <span className="overline text-[9px] tracking-[0.18em]">
          {muted ? "Attiva Audio" : "Disattiva Audio"}
        </span>
      </button>
    </div>
  );
}

export default function Marquee() {
  const items = [
    "Ho.Re.Ca. Premium",
    "Export Selezionato",
    "Micro-Vinificazioni",
    "Sicilia Sud-Orientale",
    "Allocazioni Limitate",
    "Asset-Light Model",
    "Rigore Tecnico",
    "Selezione d'Autore",
  ];
  return (
    <div className="relative bg-[#0A0A0A] text-[#FDFBF7] py-6 overflow-hidden border-y border-[#1a1a1a]">
      <div className="marquee-track">
        {[...items, ...items, ...items].map((t, i) => (
          <span
            key={i}
            className="font-serif-display italic text-2xl tracking-mid flex items-center gap-12"
          >
            {t}
            <span className="text-[#C4A671] text-xl not-italic">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export function Philosophy() {
  return (
    <section
      id="filosofia"
      data-testid="filosofia-section"
      className="relative bg-white"
    >
      <div className="max-w-[1480px] mx-auto px-6 sm:px-12 lg:px-20 py-24 sm:py-32 lg:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Image column */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="lg:col-span-5 relative zoom-frame"
          >
            <FounderVideo />
            <div className="mt-4 flex items-center justify-between max-w-[420px] mx-auto lg:mx-0">
              <span className="overline text-[10px]">Claudio Pucci</span>
              <span className="overline text-[10px]">Fondatore · Direttore</span>
            </div>

            {/* Editorial pull-quote: claim of the founder */}
            <motion.figure
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.9, delay: 0.25, ease: "easeOut" }}
              className="mt-10 pl-5 border-l-2 border-[#C4A671]"
              data-testid="founder-quote"
            >
              <blockquote
                className="font-serif-display italic leading-[1.08] text-[#0A0A0A]"
                style={{ fontSize: "clamp(1.8rem, 3.2vw, 2.6rem)" }}
              >
                Il segreto non è l&apos;uva.
              </blockquote>
              <figcaption className="overline text-[9.5px] mt-3 text-[#5C5C5C]">
                — Claudio Pucci, Fondatore
              </figcaption>
            </motion.figure>
          </motion.div>

          {/* Text column */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: "easeOut" }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-6 mb-8">
              <span className="section-num text-3xl">03</span>
              <div className="h-px w-12 bg-[#0A0A0A]" />
              <span className="overline text-[10px]">La Filosofia</span>
            </div>

            <h2
              className="font-serif-display leading-[0.95] text-[#0A0A0A]"
              style={{ fontSize: "clamp(2.5rem, 6vw, 5.5rem)" }}
            >
              La Firma,
              <br />
              <span className="italic font-light text-[#63171B]">la Visione.</span>
            </h2>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-10">
              <div className="md:col-span-7 space-y-6">
                <p className="text-[16px] leading-[1.9] text-[#2C2C2C] font-light">
                  La Valle degli Iblei è il compimento di un percorso umano e
                  professionale durato oltre un quarto di secolo. In 25 anni
                  vissuti nel cuore della viticoltura d&apos;autore presso
                  l&apos;Azienda Agricola COS,{" "}
                  <span className="font-serif-display italic text-[#0A0A0A]">
                    Claudio Pucci
                  </span>{" "}
                  ha appreso, osservato e fatto propri i valori di una grande
                  scuola artigianale.
                </p>
                <p className="text-[16px] leading-[1.9] text-[#2C2C2C] font-light">
                  Da questa solida eredità è nata la volontà di evolvere quella
                  visione: tracciare una strada personale che unisce la
                  disciplina appresa sul campo all&apos;identità del terroir
                  calcareo degli Iblei, dando vita a{" "}
                  <em className="font-serif-display text-[#0A0A0A]">
                    selezioni d&apos;élite
                  </em>{" "}
                  pensate per la grande ristorazione.
                </p>
                <p className="text-[16px] leading-[1.9] text-[#2C2C2C] font-light">
                  Attraverso un modello operativo{" "}
                  <em className="font-serif-display text-[#0A0A0A]">
                    "asset-light"
                  </em>
                  , selezioniamo personalmente le migliori micro-vinificazioni e i
                  lotti d'eccellenza della Sicilia, garantendo un rigore tecnico
                  assoluto e un posizionamento premium flessibile e su misura.
                </p>
              </div>

              <div className="md:col-span-5 space-y-8 md:pl-6 md:border-l md:border-[#EAEAEA]">
                <StatItem n="25" label="Anni di Esperienza" />
              <StatItem n="04" label="Cuvée d'Autore" />
                <StatItem n="∞" label="Allocazioni Limitate" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* ── Timeline del Fondatore: Il Percorso (scroll-pinning) ── */}
        <FounderTimeline />
      </div>
    </section>
  );
}

const TIMELINE_STEPS = [
  {
    year: "2000",
    label: "L'Inizio",
    img: "/img/timeline/tl-raccolta.jpg",
    alt: "Claudio Pucci durante la raccolta delle uve in vigna — archivio reale, bianco e nero",
    text: "I primi passi nella viticoltura d'autore presso l'Azienda Agricola COS.",
  },
  {
    year: "2000 — 2022",
    label: "La Grande Scuola",
    img: "/img/timeline/tl-anfora.jpg",
    alt: "Claudio sigilla un'anfora di terracotta — archivio reale, bianco e nero",
    text: "Oltre vent'anni di rigore e disciplina contadina: la mia scuola di vita e di vigna.",
  },
  {
    year: "2022",
    label: "La Svolta",
    img: "/img/timeline/tl-potatura.jpg",
    alt: "Potatura secca a testa a T — archivio reale, bianco e nero",
    text: "La conclusione del percorso storico da COS e la scelta di tracciare la mia strada d'autore.",
  },
  {
    year: "2024",
    label: "La Visione",
    img: "/img/timeline/tl-germoglio-api.jpg",
    alt: "Vigna in germoglio con nido di api di miele — archivio reale, bianco e nero",
    text: "La nascita de La Valle degli Iblei e la selezione dei micro-lotti sui suoli calcarei.",
  },
  {
    year: "2025",
    label: "La Maison",
    img: "/img/timeline/tl-frappato-maturo.jpg",
    alt: "Frappato maturo il 25 settembre, grappoli pronti alla vendemmia — archivio reale, bianco e nero",
    text: "Il primo Vintage in allocazione riservata: Scilla, Terra Arsa, Blanche e Claire.",
    gold: true,
  },
];

function TlNode({ i, idx, left }) {
  const background = useTransform(idx, (v) => (v >= i - 0.08 ? "#C4A671" : "#FDFBF7"));
  const scale = useTransform(idx, (v) => (Math.abs(v - i) < 0.4 ? 1.4 : 1));
  return (
    <motion.div
      aria-hidden
      className="absolute top-1/2 w-[9px] h-[9px] border border-[#C4A671]"
      style={{ left, x: "-50%", y: "-50%", rotate: 45, background, scale }}
    />
  );
}

function TlStepDesktop({ step, i, idx }) {
  const opacity = useTransform(idx, (v) => (v >= i - 0.15 ? 1 : 0.16));
  const y = useTransform(idx, (v) => (v >= i - 0.15 ? 0 : 26));
  return (
    <motion.div
      style={{ opacity, y }}
      className="px-3.5 first:pl-0 last:pr-0 will-change-transform"
      data-testid={`timeline-step-${i}`}
    >
      <div className="relative overflow-hidden mb-5" style={{ boxShadow: "0 0 0 1px rgba(196,166,113,0.35)" }}>
        <img
          src={step.img}
          alt={step.alt}
          loading="lazy"
          className="w-full h-40 xl:h-44 object-cover grayscale contrast-110"
          data-testid={`timeline-img-${i}`}
        />
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(to top, rgba(10,10,10,0.28), transparent 45%)" }}
        />
      </div>
      <div
        className="font-serif-display italic text-[24px] xl:text-[26px] leading-none"
        style={{ color: step.gold ? "#C4A671" : "#0A0A0A" }}
      >
        {step.year}
      </div>
      <div className="overline text-[9px] mt-2.5" style={{ color: "#8B7A55" }}>
        {step.label}
      </div>
      <p className="mt-3 text-[12.5px] xl:text-[13px] leading-[1.8] text-[#5C5C5C] font-light max-w-[240px]">
        {step.text}
      </p>
    </motion.div>
  );
}

function TlStepMobile({ step, i, idx }) {
  const opacity = useTransform(idx, (v) => Math.max(0, 1 - Math.abs(v - i) * 2.2));
  const x = useTransform(idx, (v) => (i - v) * 90);
  return (
    <motion.div
      style={{ opacity, x }}
      className="absolute inset-0 flex flex-col justify-start will-change-transform"
      data-testid={`timeline-step-mobile-${i}`}
    >
      <div className="relative overflow-hidden mb-6" style={{ boxShadow: "0 0 0 1px rgba(196,166,113,0.35)" }}>
        <img
          src={step.img}
          alt={step.alt}
          loading="lazy"
          className="w-full h-52 object-cover grayscale contrast-110"
        />
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(to top, rgba(10,10,10,0.28), transparent 45%)" }}
        />
      </div>
      <div
        className="font-serif-display italic text-[30px] leading-none"
        style={{ color: step.gold ? "#C4A671" : "#0A0A0A" }}
      >
        {step.year}
      </div>
      <div className="overline text-[9.5px] mt-3" style={{ color: "#8B7A55" }}>
        {step.label}
      </div>
      <p className="mt-3.5 text-[14px] leading-[1.85] text-[#5C5C5C] font-light">
        {step.text}
      </p>
    </motion.div>
  );
}

function FounderTimeline() {
  const wrapRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start start", "end end"],
  });
  const idx = useTransform(scrollYProgress, [0.06, 0.94], [0, 4], { clamp: true });
  const travel = useTransform(idx, [0, 4], ["10%", "90%"]);

  return (
    <div ref={wrapRef} className="relative h-[300vh] mt-16 sm:mt-24" data-testid="founder-timeline">
      <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">
        <div className="flex items-center gap-6 mb-10 sm:mb-12">
          <div className="h-px w-12 bg-[#C4A671]" />
          <span className="overline text-[10px] text-[#8B7A55]">
            Il Percorso — Un Quarto di Secolo di Vigna
          </span>
          <div className="h-px flex-1 bg-[#EAEAEA]" />
        </div>

        {/* Rail dorato con nodi e rombo viaggiante */}
        <div className="relative h-px mb-10 sm:mb-12" style={{ background: "rgba(196,166,113,0.32)" }}>
          {TIMELINE_STEPS.map((_, i) => (
            <TlNode key={i} i={i} idx={idx} left={`${10 + i * 20}%`} />
          ))}
          <motion.div
            aria-hidden
            className="absolute top-1/2 w-[13px] h-[13px] bg-[#C4A671]"
            style={{
              left: travel,
              x: "-50%",
              y: "-50%",
              rotate: 45,
              boxShadow: "0 0 16px rgba(196,166,113,0.85)",
            }}
            data-testid="timeline-traveler"
          />
        </div>

        {/* Desktop: 5 colonne che si attivano in sequenza */}
        <div className="hidden lg:grid grid-cols-5">
          {TIMELINE_STEPS.map((step, i) => (
            <TlStepDesktop key={i} step={step} i={i} idx={idx} />
          ))}
        </div>

        {/* Mobile: una tappa alla volta, crossfade + slide */}
        <div className="lg:hidden relative h-[430px] max-w-[420px]">
          {TIMELINE_STEPS.map((step, i) => (
            <TlStepMobile key={i} step={step} i={i} idx={idx} />
          ))}
        </div>
      </div>
    </div>
  );
}

function StatItem({ n, label }) {
  return (
    <div>
      <div className="font-serif-display text-5xl text-[#0A0A0A] leading-none">
        {n}
      </div>
      <div className="overline text-[9.5px] mt-2">{label}</div>
    </div>
  );
}
